(() => {
  const MAX_LINES = 100;
  const quantity = value => Number.isSafeInteger(Number(value)) && Number(value) >= 0 && Number(value) <= 9999 ? Number(value) : null;
  const estimate = (entries, currency) => {
    const subtotal = entries.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const eligible = entries.filter(item => !item.giftCard).reduce((sum, item) => sum + item.price * item.quantity, 0);
    const rate = currency === 'COP' ? eligible >= 100000100 ? 20 : eligible >= 50000100 ? 10 : 0 : 0;
    const discount = Math.round(eligible * rate / 100);
    return { subtotal, rate, discount, total: subtotal - discount };
  };
  const reconcile = (entries, pending, cartItems) => {
    const added = new Map();
    cartItems.filter(item => item.properties?._ak_bulk_batch === pending.id).forEach(item => added.set(String(item.variant_id), (added.get(String(item.variant_id)) || 0) + item.quantity));
    const next = entries.map(item => {
      const sent = pending.items.find(sentItem => String(sentItem.id) === String(item.id));
      return { ...item, quantity: Math.max(0, item.quantity - Math.min(sent?.quantity || 0, added.get(String(item.id)) || 0)) };
    }).filter(item => item.quantity > 0);
    const addedUnits = pending.items.reduce((sum, item) => sum + Math.min(item.quantity, added.get(String(item.id)) || 0), 0);
    const requested = pending.items.reduce((sum, item) => sum + item.quantity, 0);
    return { entries: next, addedUnits, complete: addedUnits === requested };
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = { quantity, estimate, reconcile };
  if (typeof window === 'undefined' || customElements.get('ak-bulk-order')) return;
  const el = (tag, className, text) => { const node = document.createElement(tag); if (className) node.className = className; if (text != null) node.textContent = text; return node; };
  class BulkOrder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.t = JSON.parse(this.querySelector('[data-bulk-i18n]').textContent);
      this.root = this.dataset.root.endsWith('/') ? this.dataset.root : this.dataset.root + '/';
      this.key = `aleko-bulk-v1:${this.dataset.store}:${this.dataset.currency}`;
      this.persist = true;
      this.busy = false;
      this.version = 0;
      this.viewVariants = new Map();
      this.mobile = window.matchMedia('(max-width: 849px)');
      this.visibleLimit = 6;
      this.dataset.mobileView = 'catalog';
      this.mediaListener = () => this.renderRows();
      this.mobile.addEventListener('change', this.mediaListener);
      this.format = new Intl.NumberFormat(this.dataset.currency === 'COP' ? 'es-CO' : this.dataset.locale, { style: 'currency', currencyDisplay: 'narrowSymbol', currency: this.dataset.currency, maximumFractionDigits: this.dataset.currency === 'COP' ? 0 : 2 });
      this.read();
      this.consume(JSON.parse(this.querySelector('[data-ak-bulk-data]').textContent));
      this.querySelector('[data-bulk-search]').addEventListener('submit', event => {
        event.preventDefault(); const q = event.currentTarget.querySelector('input').value.trim();
        const url = new URL(this.root + (q ? 'search' : 'collections/all'), location.origin);
        if (q) { url.searchParams.set('q', window.akSearchAlias ? window.akSearchAlias(q) : q); url.searchParams.set('type', 'product'); url.searchParams.set('options[prefix]', 'last'); }
        this.load(url, false, q ? `${this.t.search_results}: ${q}` : this.t.products);
      });
      this.querySelectorAll('[data-bulk-category]').forEach(link => link.addEventListener('click', event => {
        event.preventDefault(); this.querySelector('[data-bulk-search] input').value = ''; this.load(new URL(link.href), false, link.textContent);
      }));
      this.querySelector('[data-bulk-more]').addEventListener('click', () => {
        if (this.mobile.matches) {
          const buffered = this.visibleLimit < this.products.length;
          this.visibleLimit += 6;
          if (buffered) { this.renderRows(); return; }
        }
        if (this.next) this.load(new URL(this.next, this.currentURL || location.href), true);
      });
      this.querySelector('[data-bulk-submit]').addEventListener('click', () => this.submit());
      this.querySelectorAll('[data-bulk-view]').forEach(button => button.addEventListener('click', () => this.showView(button.dataset.bulkView)));
      this.querySelector('[data-bulk-continue]').addEventListener('click', () => this.showView('catalog'));
      this.storageListener = event => { if (event.key === this.key && !this.busy) { this.read(); this.render(); } };
      window.addEventListener('storage', this.storageListener);
      if (location.hash === '#pedido-rapido') this.closest('details').open = true;
      this.render();
      if (this.pending) this.status(this.t.unknown, true);
    }
    disconnectedCallback() { this.mobile?.removeEventListener('change', this.mediaListener); window.removeEventListener('storage', this.storageListener); this.controller?.abort(); this.ready = false; }
    showView(view) {
      this.dataset.mobileView = view;
      this.querySelectorAll('[data-bulk-view]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.bulkView === view)));
      this.querySelector('.akbo-mobile-nav').scrollIntoView({ block: 'start' });
      this.querySelector(`[data-bulk-view="${view}"]`).focus({ preventScroll: true });
    }
    money(cents) { return this.format.format(cents / 100); }
    read() {
      try {
        const state = JSON.parse(localStorage.getItem(this.key) || '{}');
        this.entries = Array.isArray(state.entries) ? state.entries.filter(item => /^\d+$/.test(String(item.id)) && /^[a-zA-Z0-9_-]+$/.test(item.handle) && quantity(item.quantity) > 0 && Number.isSafeInteger(item.price) && item.price >= 0).slice(0, MAX_LINES) : [];
        this.pending = state.pending && typeof state.pending.id === 'string' && Array.isArray(state.pending.items) ? state.pending : null;
        localStorage.setItem(this.key, JSON.stringify({ entries: this.entries, pending: this.pending }));
      } catch { this.entries ||= []; this.pending ||= null; this.persist = false; }
    }
    save() { try { localStorage.setItem(this.key, JSON.stringify({ entries: this.entries, pending: this.pending })); } catch { this.persist = false; } }
    status(message, error = false) { const node = this.querySelector('[data-bulk-status]'); node.textContent = message; node.classList.toggle('is-error', error); }
    consume(data, append = false) {
      if (!Array.isArray(data.products) || data.currency !== this.dataset.currency) throw new Error('Invalid catalog');
      if (!append) this.visibleLimit = 6;
      const existing = append ? this.products || [] : [];
      this.products = [...new Map([...existing, ...data.products].map(product => [product.id, product])).values()];
      this.next = data.next;
      this.total = data.total;
    }
    async load(url, append = false, title) {
      if (url.origin !== location.origin) return;
      const version = ++this.version;
      this.controller?.abort(); this.controller = new AbortController();
      url.searchParams.set('section_id', this.dataset.feed);
      this.querySelector('[data-bulk-feed-status]').textContent = this.t.loading;
      this.querySelector('[data-bulk-more]').disabled = true;
      const controller = this.controller;
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Catalog error');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const payload = html.querySelector('[data-ak-bulk-data]');
        if (!payload) throw new Error('Missing catalog');
        if (version !== this.version) return;
        this.consume(JSON.parse(payload.textContent), append); this.currentURL = url.href;
        if (title) this.querySelector('[data-bulk-heading]').textContent = title;
        this.querySelectorAll('[data-bulk-category]').forEach(link => link.setAttribute('aria-current', new URL(link.href).pathname === url.pathname ? 'page' : 'false'));
        this.querySelector('[data-bulk-feed-status]').textContent = this.products.length ? '' : this.t.no_results;
        this.renderRows();
      } catch { if (version === this.version) this.querySelector('[data-bulk-feed-status]').textContent = this.t.load_error; }
      finally { clearTimeout(timeout); if (version === this.version) this.querySelector('[data-bulk-more]').disabled = false; }
    }
    currentVariant(product) { return product.variants.find(v => String(v.id) === String(this.viewVariants.get(product.id))) || product.variants.find(v => v.available) || product.variants[0]; }
    setQuantity(product, variant, value) {
      if (this.busy || this.pending) return;
      const count = quantity(value); if (count === null) return;
      const index = this.entries.findIndex(item => String(item.id) === String(variant.id));
      if (count === 0) { if (index >= 0) this.entries.splice(index, 1); }
      else {
        if (!variant.available || variant.requires_selling_plan) return;
        if (index < 0 && this.entries.length >= MAX_LINES) { this.status(this.t.limit, true); return; }
        const item = { id: variant.id, handle: product.handle, title: product.title, variant: variant.title, sku: variant.sku || '', price: variant.price, quantity: count, giftCard: !!product.gift_card };
        if (index >= 0) this.entries[index] = item; else this.entries.push(item);
      }
      this.save(); this.status(''); this.renderSummary(); this.syncRows();
    }
    stepper(id, value, change, disabled, label) {
      const wrap = el('div', 'akbo-stepper');
      const input = el('input'); input.type = 'number'; input.min = '0'; input.max = '9999'; input.step = '1'; input.inputMode = 'numeric'; input.value = value; input.dataset.quantityId = id; input.setAttribute('aria-label', `${this.t.quantity}: ${label}`); input.disabled = disabled;
      const minus = el('button', '', '−'); minus.type = 'button'; minus.setAttribute('aria-label', `−1: ${label}`); minus.disabled = disabled;
      const plus = el('button', '', '+'); plus.type = 'button'; plus.setAttribute('aria-label', `+1: ${label}`); plus.disabled = disabled;
      input.addEventListener('input', () => { if (quantity(input.value) !== null) change(Number(input.value)); });
      input.addEventListener('change', () => { if (quantity(input.value) === null) { input.value = this.entries.find(item => String(item.id) === String(id))?.quantity || 0; return; } change(Number(input.value)); });
      minus.addEventListener('click', () => change(Math.max(0, (this.entries.find(item => String(item.id) === String(id))?.quantity || 0) - 1)));
      plus.addEventListener('click', () => change(Math.min(9999, (this.entries.find(item => String(item.id) === String(id))?.quantity || 0) + 1)));
      wrap.append(minus, input, plus); return wrap;
    }
    renderRows() {
      const list = this.querySelector('[data-bulk-results]'); list.replaceChildren();
      const visibleProducts = this.mobile.matches ? this.products.slice(0, this.visibleLimit) : this.products;
      for (const product of visibleProducts) {
        const variant = this.currentVariant(product); if (!variant) continue;
        const row = el('article', 'akbo-row'); row.dataset.variantId = variant.id;
        const selected = this.entries.find(item => String(item.id) === String(variant.id));
        const checkbox = el('input', 'akbo-check'); checkbox.type = 'checkbox'; checkbox.checked = !!selected; checkbox.disabled = !variant.available || variant.requires_selling_plan || this.busy || !!this.pending; checkbox.setAttribute('aria-label', `${this.t.select}: ${product.title}`);
        checkbox.addEventListener('change', () => this.setQuantity(product, variant, checkbox.checked ? 1 : 0));
        const picture = el('div', 'akbo-picture');
        if (product.image) { const img = el('img'); img.src = product.image; img.alt = ''; img.width = 72; img.height = 72; img.loading = 'lazy'; picture.append(img); }
        const info = el('div', 'akbo-info'); const link = el('a', '', product.title); link.href = product.url; info.append(link);
        if (variant.sku) info.append(el('small', '', `REF. ${variant.sku}`));
        if (product.variants.length > 1) {
          const select = el('select'); select.setAttribute('aria-label', `${this.t.variant}: ${product.title}`); select.disabled = this.busy || !!this.pending;
          for (const v of product.variants) { const option = el('option', '', `${v.title}${v.available ? '' : ' · ' + this.t.sold_out}`); option.value = v.id; option.selected = v.id === variant.id; select.append(option); }
          select.addEventListener('change', () => { this.viewVariants.set(product.id, select.value); this.renderRows(); }); info.append(select);
        }
        const price = el('div', 'akbo-price'); price.append(el('small', '', this.t.unit), el('strong', '', this.money(variant.price)));
        const control = el('div', 'akbo-control');
        if (!variant.available || variant.requires_selling_plan) control.append(el('span', 'akbo-unavailable', variant.requires_selling_plan ? this.t.special : this.t.sold_out));
        else control.append(this.stepper(variant.id, selected?.quantity || 0, value => this.setQuantity(product, variant, value), this.busy || !!this.pending, product.title));
        const subtotal = el('strong', 'akbo-line-total', this.money((selected?.quantity || 0) * variant.price)); subtotal.dataset.lineTotal = '';
        row.append(checkbox, picture, info, price, control, subtotal); list.append(row);
      }
      this.querySelector('[data-bulk-count]').textContent = `${visibleProducts.length} / ${this.total}`;
      this.querySelector('[data-bulk-more]').hidden = !this.next && (!this.mobile.matches || this.visibleLimit >= this.products.length);
      this.syncRows();
    }
    syncRows() {
      this.querySelectorAll('[data-bulk-results] .akbo-row').forEach(row => {
        const item = this.entries.find(item => String(item.id) === row.dataset.variantId);
        const check = row.querySelector('.akbo-check'); check.checked = !!item;
        const input = row.querySelector('[data-quantity-id]'); if (input && input !== document.activeElement) input.value = item?.quantity || 0;
        row.querySelector('[data-line-total]').textContent = this.money(item ? item.price * item.quantity : 0);
        row.classList.toggle('is-selected', !!item);
      });
    }
    renderSummary(skipList = false) {
      if (!skipList) {
      const list = this.querySelector('[data-bulk-selected]'); list.replaceChildren();
      if (!this.entries.length) list.append(el('p', 'akbo-empty', this.t.empty));
      for (const item of this.entries) {
        const row = el('div', 'akbo-selected-row'); const info = el('div'); info.append(el('strong', '', item.title));
        if (item.variant && item.variant !== 'Default Title') info.append(el('small', '', item.variant));
        const lineText = el('small', '', `${item.quantity} × ${this.money(item.price)}`); info.append(lineText);
        const remove = el('button', 'akbo-remove', '×'); remove.type = 'button'; remove.setAttribute('aria-label', `${this.t.remove}: ${item.title} ${item.variant || ''}`); remove.disabled = this.busy || !!this.pending;
        remove.addEventListener('click', () => { this.entries = this.entries.filter(entry => entry.id !== item.id); this.save(); this.render(); });
        const input = el('input'); input.type = 'number'; input.min = '0'; input.max = '9999'; input.step = '1'; input.value = item.quantity; input.setAttribute('aria-label', `${this.t.quantity}: ${item.title} ${item.variant || ''}`); input.disabled = this.busy || !!this.pending;
        input.addEventListener('input', () => { const count = quantity(input.value); if (count === null) return; item.quantity = count; lineText.textContent = `${count} × ${this.money(item.price)}`; this.save(); this.renderSummary(true); this.syncRows(); });
        input.addEventListener('change', () => { const count = quantity(input.value); if (count === null) { input.value = item.quantity; return; } item.quantity = count; this.entries = this.entries.filter(entry => entry.quantity > 0); this.save(); if (count === 0) row.remove(); this.renderSummary(true); this.syncRows(); }); info.append(input);
        row.append(info, remove); list.append(row);
      }
      }
      const units = this.entries.reduce((sum, item) => sum + item.quantity, 0);
      const totals = estimate(this.entries, this.dataset.currency);
      this.querySelector('[data-bulk-mobile-count]').textContent = String(units);
      this.querySelector('[data-bulk-mobile-total]').textContent = this.money(totals.total);
      this.querySelector('[data-bulk-summary-count]').textContent = `${this.t.refs}: ${this.entries.length} · ${this.t.units}: ${units}`;
      this.querySelector('[data-bulk-subtotal]').textContent = this.money(totals.subtotal);
      this.querySelector('[data-bulk-total]').textContent = this.money(totals.total);
      this.querySelector('[data-bulk-discount-row]').hidden = totals.rate === 0;
      this.querySelector('[data-bulk-discount-label]').textContent = `${this.t.discount} ${totals.rate}%`;
      this.querySelector('[data-bulk-discount]').textContent = '−' + this.money(totals.discount);
      this.querySelector('[data-bulk-benefit]').textContent = totals.rate ? `${this.t.benefit} ${totals.rate}%` : this.t.progress;
      this.querySelector('[data-bulk-save]').textContent = this.persist ? this.t.saved : this.t.storage_error;
      const submit = this.querySelector('[data-bulk-submit]');
      submit.disabled = this.busy || !this.persist || (!this.pending && !this.entries.length);
      submit.textContent = this.busy ? this.t.adding : this.pending ? this.t.verify : `${this.t.add_all}${units ? ' (' + units + ')' : ''}`;
    }
    render() { this.renderRows(); this.renderSummary(); }
    async json(url, options = {}) {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000), ...options });
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : this.t.error);
      return data;
    }
    async verifyPending() {
      const cart = await this.json(this.root + 'cart.js');
      const result = reconcile(this.entries, this.pending, cart.items);
      if (!result.addedUnits && !this.pending.responded) { this.status(this.t.unknown, true); return; }
      this.entries = result.entries; this.pending = null; this.save();
      this.status(result.complete ? this.t.success : this.t.partial, !result.complete);
      const icon = document.querySelector('#cart-icon-bubble');
      if (icon) {
        let bubble = icon.querySelector('.cart-count-bubble');
        if (cart.item_count > 0) {
          if (!bubble) { bubble = el('div', 'cart-count-bubble'); icon.append(bubble); }
          const visible = el('span', '', cart.item_count > 99 ? '99+' : String(cart.item_count)); visible.setAttribute('aria-hidden', 'true');
          const accessible = el('span', 'visually-hidden', `${cart.item_count} ${this.t.units}`);
          bubble.replaceChildren(visible, accessible);
        } else bubble?.remove();
      }
      this.querySelector('[data-bulk-status]').scrollIntoView({ block: 'nearest' });
    }
    async submit() {
      if (this.busy) return;
      const execute = async () => {
        this.read(); this.busy = true; this.render(); this.status(this.t.adding);
        try {
          if (this.pending) { await this.verifyPending(); return; }
          if (!this.persist || !this.entries.length) return;
          const snapshot = this.entries.map(item => ({ ...item }));
          const handles = [...new Set(snapshot.map(item => item.handle))]; const products = new Map();
          for (let i = 0; i < handles.length; i += 4) {
            const batch = await Promise.all(handles.slice(i, i + 4).map(async handle => [handle, await this.json(`${this.root}products/${encodeURIComponent(handle)}.js`)]));
            batch.forEach(([handle, product]) => products.set(handle, product));
          }
          let changed = false; const unavailable = [];
          for (const item of snapshot) {
            const product = products.get(item.handle); const variant = product.variants.find(v => String(v.id) === String(item.id));
            const displayed = this.products.find(p => p.handle === item.handle)?.variants.find(v => String(v.id) === String(item.id));
            if (displayed && variant) { displayed.price = variant.price; displayed.available = variant.available; }
            if (!variant?.available || product.requires_selling_plan) { unavailable.push(item.title); continue; }
            if (variant.price !== item.price) { const current = this.entries.find(entry => entry.id === item.id); if (current) current.price = variant.price; changed = true; }
          }
          if (changed) { this.save(); this.status(this.t.changed, true); return; }
          if (unavailable.length) { this.status(`${this.t.unavailable} ${unavailable.join(', ')}`, true); return; }
          const batchId = crypto.randomUUID();
          this.pending = { id: batchId, items: snapshot.map(item => ({ id: item.id, quantity: item.quantity })), responded: false };
          this.save(); if (!this.persist) { this.pending = null; this.status(this.t.storage_error, true); return; }
          let response;
          try {
            response = await fetch(this.root + 'cart/add.js', { method: 'POST', credentials: 'same-origin', signal: AbortSignal.timeout(30000), headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: this.pending.items.map(item => ({ ...item, properties: { _ak_bulk_batch: batchId } })) }) });
            this.pending.responded = true; this.save();
          } catch { this.status(this.t.unknown, true); }
          await this.verifyPending();
          if (response && !response.ok && this.entries.length) {
            let error; try { error = await response.json(); } catch {}
            if (typeof error?.description === 'string') this.status(`${this.t.partial} ${error.description}`, true);
          }
        } catch { this.status(this.pending ? this.t.unknown : this.t.error, true); }
        finally { this.busy = false; this.render(); }
      };
      if (navigator.locks) await navigator.locks.request(this.key, { ifAvailable: true }, async lock => { if (lock) await execute(); else this.status(this.t.busy, true); });
      else await execute();
    }
  }
  customElements.define('ak-bulk-order', BulkOrder);
})();


